import React, { useState, useEffect } from 'react';
import { getDocFromServer, doc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from './AuthProvider';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';

function parseFecha(raw) {
  if (!raw) return null;
  try {
    if (typeof raw.toDate === 'function') return raw.toDate();
    if (typeof raw.seconds === 'number') return new Date(raw.seconds * 1000);
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
}

function isMembresiaVigente(membresia) {
  if (!membresia) return null; // null = sin membresía (legado, acceso libre)
  if (membresia.estado === 'vencida' || membresia.estado === 'suspendida' || membresia.estado === 'cancelada') return false;

  const rawFechaFin = membresia.fechaFin || membresia.fechaExpiracion;
  const fin = parseFecha(rawFechaFin);

  // Fecha parseada inválida → tratamos como vencida (defensive)
  if (rawFechaFin && !fin) return false;
  // Fecha en el pasado → vencida
  if (fin && fin < new Date()) return false;
  // Prueba gratuita sin fecha → vencida (nunca debería ocurrir, pero defensivo)
  if (membresia.tipo === 'prueba_gratuita' && !fin) return false;

  const ESTADOS_ACTIVOS = ['activa', 'en_trial', 'en_prueba', 'active'];
  return ESTADOS_ACTIVOS.includes(membresia.estado);
}

export default function ProtectedClientRoute({ children }) {
  const { user, rol, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  const isMobileRoute = location.pathname.startsWith('/app/');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setChecking(false); return; }
    if (rol === 'admin') { setChecking(false); return; }

    let cancelled = false;

    (async () => {
      try {
        const snap = await getDocFromServer(doc(db, 'usuarios', user.uid));
        if (cancelled) return;

        if (!snap.exists()) {
          navigate(isMobileRoute ? '/app/solicitar-acceso' : '/registro-cliente', { replace: true });
          return;
        }

        const data = snap.data();
        const status = data.status || data.estado;
        if (status === 'pendiente' || status === 'pendiente_registro') {
          navigate(isMobileRoute ? '/app/pendiente' : '/pendiente', { replace: true });
          return;
        }

        let vigente = isMembresiaVigente(data.membresia);

        // vigente === null: no hay campo membresia en el doc (usuario sin membresía explícita).
        // Verificar colección membresias como fallback — si tiene registros vencidos, bloquear.
        if (vigente === null) {
          const membSnap = await getDocs(query(
            collection(db, 'membresias'),
            where('userId', '==', user.uid)
          ));
          if (!membSnap.empty) {
            const anyValid = membSnap.docs.some(d => isMembresiaVigente(d.data()) !== false);
            if (!anyValid) vigente = false;
          }
        }

        if (vigente === false) {
          navigate(isMobileRoute ? '/app/membresia-vencida' : '/membresia-requerida', { replace: true });
          return;
        }

        if (!cancelled) setChecking(false);
      } catch {
        if (!cancelled) setChecking(false); // fail open en error de red
      }
    })();

    return () => { cancelled = true; };
  }, [authLoading, user, rol]);

  // Auth loading
  if (authLoading) return <Spinner />;

  // Not authenticated
  if (!user) {
    if (isMobileRoute) return <Navigate to="/app/acceder" replace />;
    const redirectPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectPath}`} replace />;
  }

  // Checking Firestore
  if (checking) return <Spinner label="Verificando acceso…" />;

  return children;
}

function Spinner({ label }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto mb-3" />
        {label && <p className="text-slate-400 text-sm">{label}</p>}
      </div>
    </div>
  );
}

export function withClientProtection(Component) {
  return function ProtectedComponent(props) {
    return (
      <ProtectedClientRoute>
        <Component {...props} />
      </ProtectedClientRoute>
    );
  };
}
